import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TaskUserPhone } from '../../containers/Tasks/taskUserDisplay';

const DENSITY_TIGHT = 'tight';
const DENSITY_COMPACT = 'compact';
const DENSITY_ROOMY = 'roomy';

// Title + ratio (+ tickets on screenings) above the people strip.
const HEADER_RESERVE = 64;

// Below this *block* width, a truncated name ("L…") is useless — avatar (+ phone) only.
const MIN_NAME_BLOCK_WIDTH = {
    [DENSITY_COMPACT]: 88,
    [DENSITY_ROOMY]: 110,
};

// Minimum width per person chip when names are hidden.
const CHIP_COL_MIN = {
    [DENSITY_COMPACT]: 40,
    [DENSITY_ROOMY]: 48,
};

const INITIAL_COLORS = [
    '#5b8a72',
    '#6b7c9c',
    '#9a6b6b',
    '#8a7a5b',
    '#6b8a9a',
    '#7a6b9a',
    '#8a6b7a',
    '#5b7a8a',
];

const SCALE = {
    [DENSITY_COMPACT]: {
        avatar: 18,
        // Room for crown badge above the circle inside overflow:hidden.
        row: 24,
        nameColMin: 120,
        className: 'PlanEventPeople--compact',
    },
    [DENSITY_ROOMY]: {
        avatar: 26,
        row: 34,
        nameColMin: 150,
        className: 'PlanEventPeople--roomy',
    },
};

function personKey(person) {
    return String(person?.id || person?.fullname || '');
}

function personName(person) {
    if (!person)
        return '';
    if (person.fullname)
        return person.fullname;
    return [person.firstname, person.lastname].filter(Boolean).join(' ').trim();
}

function personInitials(person) {
    const first = String(person?.firstname || '').trim();
    const last = String(person?.lastname || '').trim();
    if (first || last)
        return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

    const parts = personName(person).split(/\s+/).filter(Boolean);
    if (parts.length >= 2)
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return '?';
}

function hasRealAvatar(person) {
    return !!(person?.avatar && String(person.avatar).trim());
}

function initialsColor(key) {
    const value = String(key || '');
    let hash = 0;
    for (let i = 0; i < value.length; i += 1)
        hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
    return INITIAL_COLORS[hash % INITIAL_COLORS.length];
}

function normalizePeople(people) {
    const seen = new Set();
    const result = [];
    (people || []).forEach((person) => {
        if (!person)
            return;
        const key = personKey(person);
        if (!key || seen.has(key))
            return;
        seen.add(key);
        result.push(person);
    });
    return result;
}

/**
 * Roomy named rows when the block has real space; compact named rows otherwise;
 * avatars-only only for tiny/short blocks.
 */
function densityFromSize(width, height) {
    if (height >= 56 && width >= 88)
        return DENSITY_ROOMY;
    if (height >= 24 && width >= 36)
        return DENSITY_COMPACT;
    return DENSITY_TIGHT;
}

/**
 * Prefer keeping names readable: decide showName from the whole block width,
 * then only split into as many columns as still leave each cell wide enough.
 */
function namedLayout(width, height, density, scale) {
    const showName = width >= MIN_NAME_BLOCK_WIDTH[density];
    const colMin = showName ? scale.nameColMin : CHIP_COL_MIN[density];
    const cols = Math.max(1, Math.floor(Math.max(width, 1) / colMin));
    const rows = Math.max(1, Math.floor(Math.max(height - 2, 0) / scale.row));
    return {
        showName,
        cols,
        rows,
        maxVisible: cols * rows,
    };
}

function PersonAvatar({ person, isAdmin, size }) {
    const name = personName(person);
    const initials = personInitials(person);
    const showPhoto = hasRealAvatar(person);

    return (
        <span
            className={`PlanEventPeople__avatar${isAdmin ? ' PlanEventPeople__avatar--admin' : ''}${showPhoto ? '' : ' PlanEventPeople__avatar--initials'}`}
            style={{
                width: size,
                height: size,
                ...(showPhoto ? {} : {
                    backgroundColor: initialsColor(personKey(person)),
                    fontSize: Math.max(9, Math.round(size * 0.42)),
                }),
            }}
            title={name || initials}
        >
            {showPhoto ? (
                <img src={person.avatar} alt="" />
            ) : (
                <span className="PlanEventPeople__initials" aria-hidden="true">{initials}</span>
            )}
            {isAdmin && (
                <i className="fa fa-crown PlanEventPeople__admin-badge" aria-hidden="true" />
            )}
        </span>
    );
}

function PersonRow({ person, isAdmin, avatarSize, showName }) {
    const name = personName(person) || '—';
    return (
        <div className={`PlanEventPeople__row${showName ? '' : ' PlanEventPeople__row--avatar-only'}`}>
            <PersonAvatar person={person} isAdmin={isAdmin} size={avatarSize} />
            {showName ? (
                <span className="PlanEventPeople__identity">
                    <span className="PlanEventPeople__name" title={name}>{name}</span>
                    <TaskUserPhone user={person} />
                </span>
            ) : (
                <TaskUserPhone user={person} />
            )}
        </div>
    );
}

/**
 * Progressive people strip for plan calendar blocks (shifts + screening aggregates).
 *
 * @param {{ people?: object[], admins?: object[] }} props
 */
export default function PlanEventPeople({ people = [], admins = [] }) {
    const rootRef = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const node = rootRef.current;
        if (!node || typeof ResizeObserver === 'undefined')
            return undefined;

        // Measure the calendar event box, not this strip: self-measurement
        // oscillates between density modes as content changes height.
        const target = node.closest('.rbc-event') || node;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry)
                return;
            const { width, height } = entry.contentRect;
            const peopleHeight = Math.max(0, height - HEADER_RESERVE);
            setSize((prev) => {
                if (Math.abs(prev.width - width) < 2 && Math.abs(prev.height - peopleHeight) < 2)
                    return prev;
                return { width, height: peopleHeight };
            });
        });
        observer.observe(target);
        return () => observer.disconnect();
    }, []);

    const adminList = useMemo(() => normalizePeople(admins), [admins]);
    const assigneeList = useMemo(() => {
        const adminIds = new Set(adminList.map(personKey));
        return normalizePeople(people).filter((person) => !adminIds.has(personKey(person)));
    }, [people, adminList]);

    const density = densityFromSize(size.width, size.height);
    const combined = useMemo(
        () => [...adminList.map((p) => ({ person: p, isAdmin: true })), ...assigneeList.map((p) => ({ person: p, isAdmin: false }))],
        [adminList, assigneeList]
    );

    if (combined.length === 0)
        return <div ref={rootRef} className="PlanEventPeople PlanEventPeople--empty" />;

    if (density === DENSITY_COMPACT || density === DENSITY_ROOMY) {
        const scale = SCALE[density];
        const { showName, cols, maxVisible } = namedLayout(size.width, size.height, density, scale);
        const visible = combined.slice(0, maxVisible);
        const overflow = Math.max(0, combined.length - visible.length);
        return (
            <div
                ref={rootRef}
                className={`PlanEventPeople ${scale.className}${showName ? '' : ' PlanEventPeople--no-names'}`}
                style={{
                    gridTemplateColumns: showName
                        ? `repeat(${cols}, minmax(${scale.nameColMin}px, 1fr))`
                        : `repeat(${cols}, max-content)`,
                }}
            >
                {visible.map(({ person, isAdmin }) => (
                    <PersonRow
                        key={`${isAdmin ? 'a' : 'p'}-${personKey(person)}`}
                        person={person}
                        isAdmin={isAdmin}
                        avatarSize={scale.avatar}
                        showName={showName}
                    />
                ))}
                {overflow > 0 && (
                    <div className="PlanEventPeople__overflow">… +{overflow}</div>
                )}
            </div>
        );
    }

    const tightVisible = combined.slice(0, 3);
    return (
        <div ref={rootRef} className="PlanEventPeople PlanEventPeople--tight">
            <div className="PlanEventPeople__stack">
                {tightVisible.map(({ person, isAdmin }) => (
                    <PersonAvatar
                        key={`${isAdmin ? 'a' : 'p'}-${personKey(person)}`}
                        person={person}
                        isAdmin={isAdmin}
                        size={18}
                    />
                ))}
                {combined.length > tightVisible.length && (
                    <span className="PlanEventPeople__ellipsis">…</span>
                )}
            </div>
        </div>
    );
}
